import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { ContactMaster, ContactDescription, ContactTag } from "../types";
import {
    getContactDescription,
    saveContact,
    getContactDescriptionListener,
    getSharedContactDescriptionListener,
    createEmptyContactDescription,
    getContactTagsListener,
    createEmptyContact,
    saveContactDescription,
    deleteFact,
} from "../firebase";
import FactCard, {
    Container as FactContainer,
    CenterEditRow,
} from "./fact-card";
import SpeechRecognition from "react-speech-recognition";
import {
    CircularProgress,
    TextField,
    Menu,
    MenuItem,
    Button,
    Autocomplete,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import Dropdown from "./dropdown";
import Image from "./image";
import { CONTACT_MASTER } from "../default-types";
import { getImageUrl } from "../utils";
import { PrimaryText, Separator } from "./common";
import { ReactComponent as DaysSinceContactIcon } from "../assets/arrow_counterclockwise.svg";
import DatePicker from "./date-picker";
import { ReactComponent as MagnifyingGlassIcon } from "../assets/magnifying-glass.svg";
import debounce from "debounce";
import EditTags from "./edit-tags";
import Confirm from "./confirm";
import { Dropdown as SemanticDropdown } from "semantic-ui-react";

const Container = styled.div`
    padding-left: 2em;
    padding-bottom: 3em;
    height: 100%;
    display: block;
    flex-grow: 1;
    overflow: auto;
    background-color: white;
    & .close-row {
        display: none;
    }
    @media only screen and (max-width: 720px) {
        overflow: auto;
        max-width: 100%;
        min-width: 100%;
        padding: 0em 1em;
        width: 100%;
        z-index: 2;
        position: absolute;
        top: 0em;
        left: 0em;
        min-height: 100%;

        & .close-row {
            display: flex;
            justify-content: flex-end;
        }
        & .close-row:hover {
            cursor: pointer;
        }
    }
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    margin: 1em 0em;
    & > *:first-child {
        margin-left: auto;
    }
    & > *:last-child {
        margin-right: auto;
    }
`;

const HowYouMet = styled("span")(
    ({ theme, $isActive }: any) => `
                                 ${
                                     $isActive &&
                                     `color: ${theme.palette.primary.main};`
                                 };
`
);

const SimpleRow = styled.div`
    display: flex;
    align-items: center;
    margin: 0em;
`;

const ContactHeader = styled.div`
    background-color: white;
    position: sticky;
    top: 0em;
    width: 100%;
    z-index: 3;
    display: flex;
    flex-direction: column;
    margin-bottom: 1em;
`;

const NoFactsActionButton = styled(Button)`
    width: 100%;
    margin-bottom: 1rem;
`;

const Tag = styled.span`
    padding: 0em 0.5em;
    border: 1px solid black;
    margin: 0 0.1em;
    border-radius: 5px;
    line-height: 1.2em;
    white-space: nowrap;
`;

const EditNameTextfield = styled(TextField)`
    padding: 0;
    font-size: 1.25rem;
    font-weight: 500;
    text-align: center;

    & input::placeholder {
        padding: 0;
        font-size: 1.25rem;
        font-weight: 500;
        text-align: center;
    }
`;

interface Props {
    contact?: ContactMaster;
    isEditingContact?: boolean;
    containerProps?: React.CSSProperties;
    setContact?: (contact: ContactMaster | null) => void;
    setIsEditingContact?: (isEditingContact: boolean) => void;
    onDeleteContact?: (contact: ContactMaster) => void;
    searchByTag?: (tag: ContactTag) => void;
    searchByHowYouMet?: (howYouMet: string, howYouMetId: string) => void;
    howYouMetLookUp?: (
        concept: string
    ) => { howYouMet: string; howYouMetId: string }[];
    goToContact?: (contactId: string) => void;
    allTags: ContactTag[];
}
const factSorting = (a: ContactDescription, b: ContactDescription) => {
    if (!a.created_date && !b.created_date) return 0;
    if (!a.created_date) return 1;
    if (!b.created_date) return -1;
    if (a.created_date === b.created_date) return 0;
    if (a.created_date > b.created_date) return -1;
    return 1;
};

const DetailedContact = (props: Props) => {
    const { contact, isEditingContact, setContact } = props;
    const [tags, setTags] = useState<ContactTag[]>([]);
    const [newDescriptions, setNewDescriptions] = useState<
        ContactDescription[]
    >([]);
    const [editContact, setEditContact] =
        useState<ContactMaster>(CONTACT_MASTER);
    const [isLoadingDescription, setIsLoadingDescription] =
        useState<boolean>(true);
    const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
    const open = anchorEl ? true : false;
    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const [isChangingDaysSinceLastContact, setIsChangingDaysSinceLastContact] =
        useState<boolean>(false);
    const [daysSinceLastContactDate, setDaysSinceLastContactDate] =
        useState<string>("");
    const [isEditingHowYouMet, setIsEditingHowYouMet] =
        useState<boolean>(false);
    const [howYouMetOptions, setHowYouMetOptions] = useState<
        { howYouMet: string; howYouMetId: string }[]
    >([]);
    const handleClose = () => {
        setAnchorEl(null);
    };
    const [isEditingGroups, setIsEditingGroups] = useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [sharedDescriptions, setSharedDescriptions] = useState<
        ContactDescription[]
    >([]);
    const [ownDescriptions, setOwnDescriptions] = useState<
        ContactDescription[]
    >([]);

    const descriptions = React.useMemo(
        () =>
            [
                ...ownDescriptions,
                ...sharedDescriptions.map((desc) => ({
                    ...desc,
                    sharedFact: true,
                })),
            ].sort(factSorting),
        [ownDescriptions, sharedDescriptions]
    );

    const debouncedHowYouMetLookUp = React.useCallback(
        debounce((concept: string) => {
            if (props.howYouMetLookUp) {
                setHowYouMetOptions(props.howYouMetLookUp(concept));
            }
        }, 500),
        [props.howYouMetLookUp]
    );

    const handleSaveContact = async () => {
        await saveContact(editContact);
        setContact && setContact(editContact);
        props.setIsEditingContact && props.setIsEditingContact(false);

        setEditContact(CONTACT_MASTER);
    };

    const handleAddNewFact = async (factKind: number) => {
        const newFact = await createEmptyContactDescription();
        newFact.factKind = factKind;
        if (factKind === 3) {
            newFact.remindOnDate = "12#Months";
        }
        setNewDescriptions((descriptions) => [...descriptions, newFact]);
    };

    const handleStartRecording = async () => {
        handleAddNewFact(0);
        SpeechRecognition.startListening({
            continuous: true,
            language: "en-GB",
        });
    };

    const handleChangeKeepInTouch = async (
        evt: React.MouseEvent<HTMLLIElement>
    ) => {
        if (!contact) return;
        const newKeepInTouch = evt.currentTarget.innerText;
        await saveContact({
            ...contact,
            keepintouch: newKeepInTouch || contact.keepintouch,
        });
    };

    const handleSaveDaysSinceLastContact = async () => {
        if (!contact) return;
        await saveContact(
            {
                ...contact,
                updated_date: daysSinceLastContactDate,
            },
            undefined,
            true
        );
        setIsChangingDaysSinceLastContact(false);
    };

    const handleChangeDaysSinceLastContact = () => {
        setIsChangingDaysSinceLastContact(true);
    };

    const handleSaveHowYouMet = (howYouMet: string, howYouMetId: string) => {
        if (contact) {
            saveContact({
                ...contact,
                howyoumeet: howYouMet,
                howyoumeet_id: howYouMetId,
            });
            setIsEditingHowYouMet(false);
        }
    };

    useEffect(() => {
        if (isEditingHowYouMet)
            setTimeout(() => {
                document
                    .getElementById("how-you-met-textfield")
                    ?.scrollIntoView({ block: "center" });
                document.getElementById("how-you-met-textfield")?.focus();
            }, 10);
    }, [isEditingHowYouMet]);

    useEffect(() => {
        if (contact) {
            const unsubscribeSharedFacts = getSharedContactDescriptionListener(
                contact.original_person_id || "",
                (facts: ContactDescription[]) => {
                    setSharedDescriptions(facts.sort(factSorting));
                    setIsLoadingDescription(false);
                }
            );
            const unsubscribeDescriptions = getContactDescriptionListener(
                contact.contact_id,
                (facts: ContactDescription[]) => {
                    setOwnDescriptions(facts.sort(factSorting));
                    setIsLoadingDescription(false);
                }
            );
            const unsubscribeTags = getContactTagsListener(
                contact.contact_id,
                (tags: ContactTag[]) => {
                    setTags(
                        tags.filter(
                            (tag: ContactTag) => tag.description.length > 0
                        )
                    );
                }
            );
            return () => {
                unsubscribeDescriptions();
                unsubscribeTags();
                unsubscribeSharedFacts();
            };
        }
    }, []);

    useEffect(() => {
        if (isEditingContact && contact) {
            setEditContact(contact);
        }
    }, [isEditingContact]);

    return (
        <Container
            style={{
                ...props.containerProps,
                display: props.contact ? "block" : "none",
            }}
        >
            {!contact ? (
                <span>Select a contact</span>
            ) : (
                <div>
                    <ContactHeader>
                        <Row style={{ position: "relative" }}>
                            <div />
                            <ArrowBackIosIcon
                                className="close-row"
                                onClick={() => setContact && setContact(null)}
                            />
                            {isEditingContact ? (
                                <EditNameTextfield
                                    autoFocus={true}
                                    onBlur={handleSaveContact}
                                    placeholder="Name"
                                    inputProps={{
                                        style: {
                                            padding: "0",
                                            fontSize: "1.25rem",
                                            fontWeight: 500,
                                            textAlign: "center",
                                        },
                                    }}
                                    style={{ width: "100%" }}
                                    value={editContact.name}
                                    onChange={(
                                        evt: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        setEditContact({
                                            ...editContact,
                                            name: evt.target.value,
                                        });
                                    }}
                                />
                            ) : (
                                <span
                                    style={{
                                        fontSize: "1.25rem",
                                        lineHeight: "1.25rem",
                                        fontWeight: 500,
                                        textAlign: "center",
                                    }}
                                    onClick={() => {
                                        if (contact.original_person_id) return;
                                        props.setIsEditingContact &&
                                            props.setIsEditingContact(true);
                                    }}
                                >
                                    {contact.name}
                                </span>
                            )}
                            <Separator
                                style={{ position: "absolute", bottom: "0px" }}
                            />
                            {/*isEditingContact && (
                                <Button
                                    color="primary"
                                    onClick={handleSaveContact}
                                >
                                    Save
                                </Button>
                            )*/}
                            {!isEditingContact && (
                                <Dropdown
                                    style={{ display: "flex" }}
                                    options={
                                        <>
                                            <MenuItem
                                                onClick={() => {
                                                    setConfirmDelete(true);
                                                }}
                                            >
                                                Delete
                                            </MenuItem>
                                            <MenuItem>
                                                <HowYouMet $isActive>
                                                    Cancel
                                                </HowYouMet>
                                            </MenuItem>
                                        </>
                                    }
                                >
                                    <MoreHorizIcon
                                        style={{
                                            marginLeft: "1em",
                                            cursor: "pointer",
                                        }}
                                        onClick={handleMenuOpen}
                                    />
                                </Dropdown>
                            )}
                        </Row>
                        <Row style={{ margin: "0" }}>
                            <div />
                            <>
                                <label
                                    htmlFor="upload-contact-profile-picture"
                                    style={{ position: "relative" }}
                                >
                                    <Image
                                        src={getImageUrl(
                                            contact.person_profile
                                        )}
                                        style={{
                                            maxWidth: "3.5em",
                                            maxHeight: "3.5em",
                                            borderRadius: "50%",
                                            marginRight: "1em",
                                            cursor: "pointer",
                                            width: "100%",
                                            minHeight: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                    {isEditingContact && (
                                        <Image
                                            src="/static/ic_editProfile.png"
                                            style={{
                                                width: "1em",
                                                height: "1em",
                                                position: "absolute",
                                                bottom: "0px",
                                                right: "1em",
                                                cursor: "pointer",
                                            }}
                                        />
                                    )}
                                </label>
                                <input
                                    disabled={
                                        contact.original_person_id
                                            ? true
                                            : false
                                    }
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    id="upload-contact-profile-picture"
                                    onChange={(
                                        evt: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        if (!evt.target.files) return;
                                        saveContact(
                                            contact,
                                            evt.target.files[0]
                                        );
                                    }}
                                />
                            </>
                            <div
                                style={{
                                    margin: "0em 0.5em",
                                    textAlign: "center",
                                }}
                            >
                                {descriptions.length} <br /> Notes
                            </div>
                            <div
                                style={{
                                    margin: "0em auto 0em 0.5em",
                                    textAlign: "center",
                                }}
                            >
                                <Dropdown
                                    options={
                                        <>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Daily
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Weekly
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Bi-Weekly
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Monthly
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Quarterly
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Half-Yearly
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Yearly
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Once in a While
                                            </MenuItem>
                                            <MenuItem
                                                onClick={
                                                    handleChangeKeepInTouch
                                                }
                                            >
                                                Do Not
                                            </MenuItem>
                                        </>
                                    }
                                >
                                    <PrimaryText
                                        style={{
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {contact.keepintouch || "Randomly"}
                                    </PrimaryText>
                                </Dropdown>
                                <span> Keep In Touch</span>
                            </div>
                        </Row>
                        <SimpleRow
                            style={{
                                position: "relative",
                                cursor: "pointer",
                                alignSelf: "flex-start",
                            }}
                            onClick={handleChangeDaysSinceLastContact}
                        >
                            {contact.updated_date
                                ? Math.max(
                                      0,
                                      Math.floor(
                                          (new Date().getTime() -
                                              new Date(
                                                  contact.updated_date || ""
                                              ).getTime()) /
                                              1000 /
                                              60 /
                                              60 /
                                              24
                                      )
                                  )
                                : 0}{" "}
                            days since last contact
                            <DaysSinceContactIcon
                                style={{ marginLeft: "0.2em" }}
                            />
                        </SimpleRow>

                        <SimpleRow
                            style={{
                                margin: "0",
                                position: "relative",
                                justifyContent: "space-between",
                            }}
                        >
                            <span>Groups</span>
                            {!isEditingGroups && (
                                <Button
                                    onClick={() => {
                                        setIsEditingGroups(true);
                                    }}
                                    style={{ marginLeft: "auto" }}
                                >
                                    Manage groups
                                </Button>
                            )}
                            <Separator
                                style={{ position: "absolute", top: "0px" }}
                            />
                        </SimpleRow>
                        {!isEditingGroups ? (
                            <SimpleRow
                                style={{
                                    overflowX: "auto",
                                    width: "100%",
                                    marginTop: "0",
                                    marginBottom: "0.5rem",
                                }}
                            >
                                {tags.map((tag: ContactTag) => (
                                    <Tag
                                        key={tag.tag_id}
                                        onClick={() => {
                                            props.searchByTag &&
                                                props.searchByTag(tag);
                                            if (
                                                window.document.body
                                                    .clientWidth < 720
                                            ) {
                                                setContact && setContact(null);
                                            }
                                        }}
                                    >
                                        {tag.description}
                                    </Tag>
                                ))}
                            </SimpleRow>
                        ) : (
                            <SimpleRow>
                                <EditTags
                                    closeEditTags={() =>
                                        setIsEditingGroups(false)
                                    }
                                    contact={contact}
                                    tags={tags}
                                    allTags={props.allTags}
                                />
                            </SimpleRow>
                        )}
                        <Separator
                            style={{ position: "absolute", bottom: "0px" }}
                        />
                    </ContactHeader>

                    {!isEditingHowYouMet && contact.howyoumeet && (
                        <FactContainer
                            onClick={() => {
                                if (!contact) return;
                                setIsEditingHowYouMet(true);
                                setEditContact({
                                    ...editContact,
                                    contact_id: contact.contact_id,
                                    howyoumeet: contact.howyoumeet,
                                    howyoumeet_id: contact.howyoumeet_id,
                                });
                            }}
                        >
                            <b>How you met:</b>{" "}
                            <HowYouMet
                                $isActive={true}
                                onClick={(evt: React.SyntheticEvent) => {
                                    if (!contact) return;
                                    evt.stopPropagation();
                                    evt.preventDefault();
                                    if (!contact.howyoumeet_id) {
                                        props.searchByHowYouMet &&
                                            props.searchByHowYouMet(
                                                contact.howyoumeet,
                                                contact.howyoumeet_id
                                            );
                                        if (
                                            window.document.body.clientWidth <
                                            720
                                        ) {
                                            setContact && setContact(null);
                                        }
                                        return;
                                    }
                                    props.goToContact &&
                                        props.goToContact(
                                            contact.howyoumeet_id
                                        );
                                }}
                            >
                                {contact.howyoumeet}
                            </HowYouMet>
                            <div
                                style={{
                                    position: "absolute",
                                    right: "2.2rem",
                                    top: "0rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                }}
                            >
                                <MagnifyingGlassIcon
                                    style={{ cursor: "pointer" }}
                                    onClick={(evt: React.SyntheticEvent) => {
                                        evt.stopPropagation();
                                        if (!contact) return;
                                        props.searchByHowYouMet &&
                                            props.searchByHowYouMet(
                                                contact.howyoumeet,
                                                contact.howyoumeet_id
                                            );
                                        if (
                                            window.document.body.clientWidth <
                                            720
                                        ) {
                                            setContact && setContact(null);
                                        }
                                    }}
                                />
                            </div>
                        </FactContainer>
                    )}
                    {isEditingHowYouMet && (
                        <FactContainer $isEditing={true}>
                            <SemanticDropdown
                                options={howYouMetOptions.map(
                                    (
                                        option: {
                                            howYouMet: string;
                                            howYouMetId: string;
                                        },
                                        index: number
                                    ) => ({
                                        key: `${option.howYouMet}-${option.howYouMetId}`,
                                        value: `${option.howYouMet}-${option.howYouMetId}`,
                                        text: option.howYouMet,
                                    })
                                )}
                                search
                                value={`${editContact.howyoumeet}-${editContact.howyoumeet_id}`}
                                fluid
                                placeholder="Add How You Met"
                                defaultOpen
                                onOpen={(
                                    evt: React.SyntheticEvent,
                                    data: any
                                ) => {
                                    debouncedHowYouMetLookUp(
                                        editContact.howyoumeet
                                    );
                                }}
                                defaultSearchQuery={editContact.howyoumeet}
                                onChange={async (
                                    evt: React.SyntheticEvent,
                                    data: any
                                ) => {
                                    if (data.value.includes("Add Person")) {
                                        if (!props.contact) return;
                                        const contact =
                                            await createEmptyContact();
                                        contact.user_id = props.contact.user_id;
                                        contact.name = data.value
                                            .split("-")[0]
                                            .substr("Add Person ".length);
                                        handleSaveHowYouMet(
                                            contact.name,
                                            contact.contact_id
                                        );
                                        saveContact(contact);
                                    } else if (
                                        data.value.includes("Add Entity")
                                    ) {
                                        const entityName = data.value
                                            .split("-")[0]
                                            .substr("Add Entity ".length);
                                        handleSaveHowYouMet(entityName, "");
                                    } else {
                                        handleSaveHowYouMet(
                                            data.value.split("-")[0],
                                            data.value.split("-")[1]
                                        );
                                    }
                                }}
                                onSearchChange={(
                                    evt: React.SyntheticEvent,
                                    data: any
                                ) => {
                                    debouncedHowYouMetLookUp(
                                        data.searchQuery || ""
                                    );
                                }}
                            />
                            {/*
                            <Autocomplete
                                onChange={async (
                                    evt: any,
                                    newValue: {
                                        howYouMet: string;
                                        howYouMetId: string;
                                    } | null
                                ) => {
                                    if (newValue === null) return;
                                    if (
                                        newValue.howYouMet ===
                                            newValue.howYouMetId &&
                                        newValue.howYouMet.endsWith(
                                            editContact.howyoumeet
                                        )
                                    ) {
                                        // add new person or add new entity
                                        if (
                                            newValue.howYouMet.startsWith(
                                                "Add Person"
                                            )
                                        ) {
                                            if (!props.contact) return;
                                            const contact =
                                                await createEmptyContact();
                                            contact.user_id =
                                                props.contact.user_id;
                                            contact.name =
                                                newValue.howYouMet.substr(
                                                    "Add Person".length
                                                );
                                            handleSaveHowYouMet(
                                                contact.name,
                                                contact.contact_id
                                            );
                                            saveContact(contact);
                                            // add new person
                                        } else {
                                            handleSaveHowYouMet(
                                                newValue.howYouMet.split(
                                                    " "
                                                )[2],
                                                ""
                                            );
                                            // add new entity
                                        }
                                    } else {
                                        handleSaveHowYouMet(
                                            newValue.howYouMet,
                                            newValue.howYouMetId
                                        );
                                        // select from an existing how you met
                                    }
                                }}
                                defaultInputValue={editContact.howyoumeet}
                                onInputChange={(
                                    evt: any,
                                    newValue: string | null,
                                    reason: string
                                ) => {
                                    if (reason === "reset") return;
                                    /*
                                    setEditContact({
                                        ...editContact,
                                        howyoumeet: newValue || "",
                                    });
                                    debouncedHowYouMetLookUp(newValue || "");
                                }}
                                getOptionLabel={(option) => option.howYouMet}
                                options={howYouMetOptions}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        key={params.id}
                                        id="how-you-met-textfield"
                                        label="How you met"
                                    />
                                )}
                            />
                                */}
                            <CenterEditRow
                                style={{
                                    position: "absolute",
                                    bottom: "0",
                                    right: "0",
                                    justifyContent: "space-between",
                                    width: "100%",
                                }}
                            >
                                <div>
                                    <Button
                                        color="error"
                                        onClick={() => {
                                            saveContact({
                                                ...contact,
                                                howyoumeet: "",
                                            });
                                            setIsEditingHowYouMet(false);
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </div>
                                <div>
                                    <Button
                                        color="grey2"
                                        onClick={() => {
                                            setIsEditingHowYouMet(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </CenterEditRow>
                        </FactContainer>
                    )}
                    <div style={{ overflow: "auto", marginBottom: "150px" }}>
                        {descriptions.length === 0 && isLoadingDescription && (
                            <FactContainer
                                style={{
                                    justifyContent: "center",
                                    display: "flex",
                                    border: "0",
                                }}
                            >
                                <CircularProgress />
                            </FactContainer>
                        )}
                        {newDescriptions.length === 0 &&
                            isEditingHowYouMet === false &&
                            descriptions.length === 0 &&
                            contact.howyoumeet === "" && (
                                <>
                                    <NoFactsActionButton
                                        variant="contained"
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            handleAddNewFact(0);
                                        }}
                                    >
                                        Add A Note
                                    </NoFactsActionButton>
                                    <NoFactsActionButton
                                        variant="contained"
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            if (!contact) return;
                                            setIsEditingHowYouMet(true);
                                            setEditContact({
                                                ...editContact,
                                                contact_id: contact.contact_id,
                                                howyoumeet: contact.howyoumeet,
                                                howyoumeet_id:
                                                    contact.howyoumeet_id,
                                            });
                                        }}
                                    >
                                        Add How You Met
                                    </NoFactsActionButton>
                                    <NoFactsActionButton
                                        variant="contained"
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            handleAddNewFact(3);
                                        }}
                                    >
                                        Add A Birthday
                                    </NoFactsActionButton>
                                    <NoFactsActionButton
                                        variant="contained"
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            handleAddNewFact(2);
                                        }}
                                    >
                                        Add A Child
                                    </NoFactsActionButton>
                                    <NoFactsActionButton
                                        variant="contained"
                                        style={{ width: "100%" }}
                                        onClick={() => {
                                            handleAddNewFact(1);
                                        }}
                                    >
                                        Add A Location
                                    </NoFactsActionButton>
                                </>
                            )}

                        {descriptions.length > 0 &&
                            descriptions.map(
                                (description: ContactDescription) => (
                                    <FactCard
                                        deleteFact={deleteFact}
                                        saveContactDescription={
                                            saveContactDescription
                                        }
                                        key={description.desc_id}
                                        fact={description}
                                    />
                                )
                            )}

                        {newDescriptions.map(
                            (description: ContactDescription) => (
                                <FactCard
                                    saveContactDescription={
                                        saveContactDescription
                                    }
                                    deleteFact={deleteFact}
                                    newFact={true}
                                    key={description.desc_id}
                                    fact={description}
                                    contactId={contact.contact_id}
                                    discardNewFact={(desc_id: string) => {
                                        setNewDescriptions((newDescriptions) =>
                                            newDescriptions.filter(
                                                (
                                                    description: ContactDescription
                                                ) =>
                                                    description.desc_id !==
                                                    desc_id
                                            )
                                        );
                                    }}
                                />
                            )
                        )}
                    </div>
                </div>
            )}
            <div
                className="gap-2"
                style={{
                    position: "fixed",
                    bottom: "1em",
                    right: "1em",
                    display: "flex",
                    justifyContent: "end",
                    flexDirection: "column",
                    paddingRight: "1em",
                }}
            >
                <Button
                    style={{
                        borderRadius: "50%",
                        fontWeight: "500",
                        minWidth: "1.5em",
                        minHeight: "1.5em",
                        width: "1.5em",
                        height: "1.5em",
                        fontSize: "2em",
                        alignItems: "unset",
                        lineHeight: "1.1em",
                    }}
                    variant="contained"
                    onClick={() => handleStartRecording()}
                >
                    <KeyboardVoiceIcon className="absolute translate-y-1/4" />
                </Button>
                <Dropdown
                    options={
                        <>
                            <MenuItem onClick={() => handleAddNewFact(0)}>
                                Add a note
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    if (!contact) return;
                                    setIsEditingHowYouMet(true);
                                    setEditContact({
                                        ...editContact,
                                        contact_id: contact.contact_id,
                                        howyoumeet: contact.howyoumeet,
                                        howyoumeet_id: contact.howyoumeet_id,
                                    });
                                }}
                            >
                                Add how you met
                            </MenuItem>
                            <MenuItem onClick={() => handleAddNewFact(2)}>
                                Add a child
                            </MenuItem>
                            <MenuItem onClick={() => handleAddNewFact(1)}>
                                Add a location
                            </MenuItem>
                            <MenuItem onClick={() => handleAddNewFact(3)}>
                                Add a birthday
                            </MenuItem>
                        </>
                    }
                >
                    <Button
                        style={{
                            borderRadius: "50%",
                            fontWeight: "500",
                            minWidth: "1.5em",
                            minHeight: "1.5em",
                            width: "1.5em",
                            height: "1.5em",
                            fontSize: "2em",
                            alignItems: "unset",
                            lineHeight: "1.1em",
                        }}
                        variant="contained"
                    >
                        +
                    </Button>
                </Dropdown>
            </div>
            {isChangingDaysSinceLastContact && (
                <div style={{ display: "none" }}>
                    <DatePicker
                        autoFocus
                        label={"Last contact"}
                        onChangeFormat={"YYYY-MM-DD"}
                        defaultValue={contact && contact.updated_date}
                        onChange={(date: string) => {
                            setDaysSinceLastContactDate(`${date} 00:00:00`);
                        }}
                        onAccept={() => {
                            handleSaveDaysSinceLastContact();
                        }}
                        onClose={() => {
                            setIsChangingDaysSinceLastContact(false);
                        }}
                    />
                </div>
            )}
            {confirmDelete && (
                <Confirm
                    open={true}
                    confirmText="Delete"
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={() => {
                        contact &&
                            props.onDeleteContact &&
                            props.onDeleteContact(contact);
                        setConfirmDelete(false);
                    }}
                />
            )}
        </Container>
    );
};

export default DetailedContact;
